import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@ouropix/ui';
import { Loader2, TrendingDown, Users, Phone, Mail, Wallet } from 'lucide-react';
import { FunnelFilters } from '@/components/users/FunnelFilters';
import { FunnelChart } from '@/components/users/FunnelChart';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';

export const Route = createFileRoute('/_admin/funil')({
  component: FunilPage,
});

interface FunnelData {
  cpfCount: number;
  phoneCount: number;
  emailCount: number;
  depositCount: number;
}

interface BTag {
  id: string;
  code: number;
  name: string;
}

interface DateRange {
  from: Date;
  to: Date;
}

function FunilPage() {
  const today = new Date();
  const sevenDaysAgo = subDays(today, 7);

  const [regRange, setRegRange] = useState<DateRange>({ from: sevenDaysAgo, to: today });
  const [actRange, setActRange] = useState<DateRange>({ from: sevenDaysAgo, to: today });
  const [selectedBtag, setSelectedBtag] = useState('all');
  const [funnelData, setFunnelData] = useState<FunnelData | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const { data: btags = [] } = useQuery({
    queryKey: ['admin-btags-active'],
    queryFn: () =>
      api.get<{ btags: BTag[] }>('/affiliate/btags', { params: { active_only: true } }).then((r) => r.data.btags || []),
    staleTime: 300_000,
  });

  const { mutate: fetchFunnel, isPending: isLoading } = useMutation({
    mutationFn: () =>
      api.post<FunnelData>('/admin/marketing/funnel', {
        registration_date_from: startOfDay(regRange.from).toISOString(),
        registration_date_to: endOfDay(regRange.to).toISOString(),
        action_date_from: startOfDay(actRange.from).toISOString(),
        action_date_to: endOfDay(actRange.to).toISOString(),
        btag_code: selectedBtag !== 'all' ? parseInt(selectedBtag) : undefined,
      }).then((r) => r.data),
    onSuccess: (data) => {
      setFunnelData(data);
      setHasSearched(true);
    },
    onError: (err) => {
      toast.error(apiErrorMessage(err, 'Erro ao buscar dados do funil'));
    },
  });

  const handleRegRangeChange = (range: DateRange) => {
    setRegRange(range);
    // keep action range start >= registration range start
    if (actRange.from < range.from) {
      setActRange({ from: range.from, to: actRange.to < range.from ? today : actRange.to });
    }
  };

  const funnelSteps = funnelData
    ? [
        { label: 'CPF', count: funnelData.cpfCount, icon: Users, color: 'bg-blue-500' },
        { label: 'Celular', count: funnelData.phoneCount, icon: Phone, color: 'bg-green-500' },
        { label: 'Email', count: funnelData.emailCount, icon: Mail, color: 'bg-purple-500' },
        { label: '1º Deposito', count: funnelData.depositCount, icon: Wallet, color: 'bg-primary' },
      ]
    : [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <TrendingDown className="h-6 w-6" />
          Funil de Conversão
        </h1>
        <p className="text-muted-foreground">Analise a jornada de cadastro e identifique pontos de abandono</p>
      </div>

      <FunnelFilters
        registrationRange={regRange}
        actionRange={actRange}
        selectedBtag={selectedBtag}
        btags={btags}
        onRegistrationRangeChange={handleRegRangeChange}
        onActionRangeChange={setActRange}
        onBtagChange={setSelectedBtag}
        onSearch={() => fetchFunnel()}
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5" />
            Funil de Cadastro
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : !hasSearched ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              Selecione os filtros e clique em &quot;Buscar&quot; para visualizar o funil
            </div>
          ) : funnelSteps.length > 0 ? (
            <FunnelChart steps={funnelSteps} />
          ) : null}
        </CardContent>
      </Card>

      {hasSearched && funnelSteps.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {funnelSteps.map((step, index) => {
            const prevCount = index > 0 ? funnelSteps[index - 1].count : step.count;
            const dropRate = prevCount > 0 ? ((prevCount - step.count) / prevCount * 100).toFixed(1) : '0';
            const conversionRate = funnelSteps[0].count > 0
              ? ((step.count / funnelSteps[0].count) * 100).toFixed(1)
              : '0';

            return (
              <Card key={step.label}>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-2 rounded-lg ${step.color}`}>
                      <step.icon className="h-4 w-4 text-white" />
                    </div>
                    <span className="text-sm font-medium">{step.label}</span>
                  </div>
                  <div className="text-2xl font-bold">{step.count.toLocaleString('pt-BR')}</div>
                  <div className="flex justify-between text-xs text-muted-foreground mt-1">
                    <span>{conversionRate}% do total</span>
                    {index > 0 && (
                      <span role="alert" className="text-destructive">-{dropRate}% drop</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
